import React from 'react';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { COLORS } from '../../Styles/Colors';
import { FORM } from './formStyles';

const ActionsBar = styled.div<{ $align?: 'left' | 'center' | 'right' }>`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: ${({ $align = 'right' }) =>
    $align === 'left' ? 'flex-start' : $align === 'center' ? 'center' : 'flex-end'};
  padding: 20px 32px;
  border-top: 1px solid ${FORM.border};
  background: ${FORM.surface};

  @media (max-width: 640px) {
    padding: 16px;
    flex-direction: column-reverse;

    & > button {
      width: 100%;
    }
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 120px;
  padding: 10px 24px;
  background-color: ${COLORS.teal};
  color: ${COLORS.white};
  border: none;
  border-radius: ${FORM.fieldRadius};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;

  &:hover:not(:disabled) {
    background-color: ${COLORS.darkTeal};
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #d1d5db;
    color: #9ca3af;
    cursor: not-allowed;
    transform: none;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  padding: 10px 24px;
  background-color: ${COLORS.white};
  color: ${FORM.text};
  border: 1px solid ${FORM.border};
  border-radius: ${FORM.fieldRadius};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background-color: ${FORM.surface};
    border-color: ${COLORS.teal};
    color: ${COLORS.teal};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface FormActionsProps {
  submitLabel?: string;
  cancelLabel?: string;
  onCancel?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  align?: 'left' | 'center' | 'right';
  hideCancel?: boolean;
  children?: React.ReactNode;
}

export const FormActions: React.FC<FormActionsProps> = ({
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  onCancel,
  isLoading = false,
  disabled = false,
  align = 'right',
  hideCancel = false,
  children,
}) => (
  <ActionsBar $align={align}>
    {children ?? (
      <>
        {!hideCancel && onCancel && (
          <SecondaryButton type="button" onClick={onCancel} disabled={isLoading}>
            {cancelLabel}
          </SecondaryButton>
        )}
        <PrimaryButton type="submit" disabled={isLoading || disabled}>
          {isLoading && <CircularProgress size={16} sx={{ color: '#fff' }} />}
          {isLoading ? 'Saving...' : submitLabel}
        </PrimaryButton>
      </>
    )}
  </ActionsBar>
);

export { PrimaryButton as FormSubmitButton, SecondaryButton as FormCancelButton };
